<template>
  <div
    class="tp-cell"
    :class="{
      'tp-cell--label': cell.type === 'label',
      'tp-cell--reference': cell.type === 'reference',
      'tp-cell--carry': transfer !== null,
      'tp-cell--selected': selected,
      'tp-cell--hidden': !cell.visible,
      'tp-cell--readonly': isReadonly,
      'tp-cell--invalid': valueError !== '',
    }"
    :tabindex="rootTabIndex"
    @click="onCellClick"
    @focus="onCellFocus"
    @keydown.enter="onCellEnter"
  >
    <!-- 見出し -->
    <div class="tp-cell__head">
      <span class="tp-cell__name">{{ cell.name }}</span>
      <span v-if="cell.required && isInput" class="tp-cell__required">必須</span>
      <!-- 設定側のチェックは「Excelに出力する」なので、裏返しの語で揃える -->
      <span v-if="!cell.visible" class="tp-cell__badge tp-cell__badge--hidden">Excel非出力</span>
      <span v-if="cell.type === 'reference'" class="tp-cell__badge tp-cell__badge--reference">
        参照
      </span>
      <span v-if="transfer" class="tp-cell__badge tp-cell__badge--carry">転記</span>
      <v-spacer />
      <span
        v-if="commentCount > 0"
        class="tp-cell__badge tp-cell__badge--comment"
        :title="`未解決のコメントが${commentCount}件あります`"
      >
        <v-icon size="12">mdi-comment-text-outline</v-icon>
        {{ commentCount }}
      </span>
    </div>

    <!-- 本体 -->
    <div v-if="cell.type === 'label'" class="tp-cell__label-body"></div>

    <div v-else-if="cell.type === 'reference'" class="tp-cell__body">
      <div v-if="reference && reference.resolution === 'resolved'" class="tp-cell__reference-value">
        {{ reference.value || "（参照元が未入力です）" }}
      </div>
      <div v-else class="tp-cell__reference-warning">
        {{ referenceWarning }}
      </div>
      <div v-if="reference && reference.sourcePlanLabel" class="tp-cell__reference-source">
        <span>参照元: {{ reference.sourcePlanLabel }}</span>
        <!--
          以前はタブ順から外していたが、参照セルは編集できずセル本体もタブ順に
          入らないため、参照元をキーボードで開く手段が無くなっていた。
          セル送りの邪魔になるタブ停止が増えるより、到達できないほうが問題。
        -->
        <a
          v-if="reference.sourcePlanId"
          href="#"
          class="ml-2"
          @click.stop.prevent="$emit('jump-source', reference.sourcePlanId)"
        >
          開く
        </a>
      </div>
    </div>

    <div v-else class="tp-cell__body">
      <!--
        長文・短文はモックの「クリックで型別エディタがその場に出現」に合わせる。
        常時テキストエリアにすると長文が固定高に埋もれて読めないため、
        非編集時は本文をそのまま流し込んで高さを内容に合わせる。
      -->
      <template v-if="isTextType">
        <div
          v-if="!editing"
          class="tp-cell__text"
          :class="{
            'tp-cell__text--empty': !draft,
            'tp-cell__text--required': !draft && cell.required && !isReadonly,
          }"
        >
          {{ draft || displayPlaceholder }}
        </div>

        <template v-else>
          <textarea
            v-if="cell.type === 'longText'"
            ref="input"
            class="tp-cell__input tp-cell__input--long"
            :value="draft"
            :rows="editorRows"
            placeholder="入力してください"
            @input="onInput"
            @compositionstart="composing = true"
            @compositionend="onCompositionEnd"
            @focus="focused = true"
            @blur="onBlur"
            @keydown.esc.stop="onEscKey"
            @click.stop
          ></textarea>

          <input
            v-else
            ref="input"
            class="tp-cell__input"
            type="text"
            :value="draft"
            placeholder="入力してください"
            @input="onInput"
            @compositionstart="composing = true"
            @compositionend="onCompositionEnd"
            @focus="focused = true"
            @blur="onBlur"
            @keydown.esc.stop="onEscKey"
            @keydown.enter.stop="onEnterKey"
            @click.stop
          />

          <!--
            上限はサーバの検証値。超えたまま送ると自動保存が理由の出ないまま
            失敗し続けるため、残り文字数を常に見せて超過前に気づけるようにする。
          -->
          <div class="tp-cell__counter" :class="{ 'tp-cell__counter--over': valueError !== '' }">
            <template v-if="valueMaxLength !== null">
              {{ draft.length.toLocaleString() }} / {{ valueMaxLength.toLocaleString() }}文字
            </template>
            <template v-else>{{ draft.length }}文字</template>
            ・Escで閉じる
          </div>
        </template>
      </template>

      <select
        v-else-if="cell.type === 'select'"
        class="tp-cell__input"
        :value="draft"
        :disabled="isReadonly"
        @change="onSelectChange"
        @focus="onControlFocus"
        @click.stop="onControlFocus"
      >
        <option value="">未選択</option>
        <option v-for="option in cell.options || []" :key="option" :value="option">
          {{ option }}
        </option>
      </select>

      <input
        v-else-if="cell.type === 'date'"
        class="tp-cell__input"
        type="date"
        :value="draft"
        :disabled="isReadonly"
        @change="onSelectChange"
        @focus="onControlFocus"
        @click.stop="onControlFocus"
      />

      <!-- 空の数値入力は枠も文字も出ず入力できることが分からないため、必ず案内を出す -->
      <input
        v-else-if="cell.type === 'number'"
        class="tp-cell__input"
        type="number"
        :value="draft"
        :disabled="isReadonly"
        :placeholder="isReadonly ? '—' : '数値を入力'"
        @input="onInput"
        @focus="onNumberFocus"
        @blur="onBlur"
        @click.stop="onControlFocus"
      />

      <!--
        サーバが弾く値はキューに積まないので、直さない限り保存されない。
        編集を閉じても消えないよう、本体側に理由を出し続ける。
      -->
      <div v-if="valueError" class="tp-cell__error">
        <v-icon size="12">mdi-alert-circle-outline</v-icon>
        {{ valueError }}（このままでは保存されません）
      </div>

      <div
        v-if="transfer"
        class="tp-cell__transfer-note"
        :class="{ 'tp-cell__transfer-note--unresolved': transferWarning !== '' }"
      >
        <v-icon size="12">mdi-arrow-down-bold-outline</v-icon>
        <!--
          転記元の帳票が特定できていても引けないことがある (そのレイアウトに項目が無い等)。
          転記元名だけ出すと引けない理由が分からないので、引けないときは理由を優先して出す
          (帳票へは右の「開く」から辿れる)
        -->
        <span v-if="transferWarning">{{ transferWarning }}</span>
        <span v-else-if="transfer.sourcePlanLabel">転記元: {{ transfer.sourcePlanLabel }}</span>
        <span v-else>上位計画から引き込み</span>
        <!--
          転記セルは編集セルでもあるため、ここをタブ順に入れると
          Tabのセル送りが毎回リンクで止まって入力作業が壊れる。
          引き込みは一括転記モーダルからキーボードで実行できる。
          (参照セルは編集できずセル送りに関係しないので、あちらは順に入れている)
        -->
        <a
          v-if="transfer.sourcePlanId"
          href="#"
          class="ml-1"
          tabindex="-1"
          @click.stop.prevent="$emit('jump-source', transfer.sourcePlanId)"
        >
          開く
        </a>
        <a
          v-if="transfer.canPull && editable"
          href="#"
          class="ml-1"
          tabindex="-1"
          @click.stop.prevent="$emit('transfer-cell', cell.itemKey)"
        >
          引き込む
        </a>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import {
  NurseryTeachingPlanLayoutCell,
  NurseryTeachingPlanResolvedReference,
} from "chaild-api/lib";
import TeachingPlanUtil from "@/utils/TeachingPlanUtil";
import { resolutionLabelOf } from "@/constants/teachingPlan";

/** carry転記先セルの表示情報 */
export type TeachingPlanCellTransfer = {
  sourcePlanId: string | null;
  sourcePlanLabel: string | null;
  canPull: boolean;
  /** 転記元の解決状態。resolved以外は引き込めない理由を出す */
  resolution: NurseryTeachingPlanResolvedReference["resolution"];
};

/**
 * 指導計画エディタの1セル。
 *
 * 日本語入力の変換確定前に値を確定させないため、compositionstart/compositionend を見て
 * 変換中は change を出さない。フォーカス中は親からの値でdraftを上書きしない
 * (自動保存の応答で入力途中の文字が巻き戻るのを防ぐ)。
 */
export default defineComponent({
  name: "TeachingPlanCell",

  props: {
    cell: {
      type: Object as PropType<NurseryTeachingPlanLayoutCell>,
      required: true,
    },
    value: {
      type: [String, null] as PropType<string | null>,
      default: null,
    },
    reference: {
      type: [Object, null] as PropType<NurseryTeachingPlanResolvedReference | null>,
      default: null,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    commentCount: {
      type: Number,
      default: 0,
    },
    /**
     * carryルールの転記先セルであることを示す情報。
     * 転記先でないセルは null。canPull は「未入力かつ転記元が解決済み」のとき true
     * (引き込み済み・入力済みでも転記セルであること自体は表示し続ける)
     */
    transfer: {
      type: [Object, null] as PropType<TeachingPlanCellTransfer | null>,
      default: null,
    },
  },

  emits: ["select", "change", "jump-source", "transfer-cell"],

  data() {
    return {
      draft: this.value ?? "",
      composing: false,
      focused: false,
      /** 長文・短文セルがインプレース編集中か */
      editing: false,
      /** Escで閉じた直後にセル本体へ戻すフォーカスで再度開かないようにする */
      suppressFocusEdit: false,
    };
  },

  computed: {
    isInput(): boolean {
      return this.cell.type !== "label" && this.cell.type !== "reference";
    },
    isReadonly(): boolean {
      return !this.editable || !this.isInput;
    },
    /** クリックでエディタを出す対象 (選択肢・日付・数値は1行で読めるため常時コントロール) */
    isTextType(): boolean {
      return this.cell.type === "longText" || this.cell.type === "shortText";
    },
    displayPlaceholder(): string {
      if (this.isReadonly) {
        return "—";
      }
      return this.cell.required ? "未入力（必須）" : "クリックして入力";
    },
    /**
     * セル本体のタブ順。
     * 編集中はセル本体とエディタの両方がタブ順に入ると、エディタからの
     * Shift+Tab が自セルへ戻ってそのまま再度開き、前のセルへ抜けられなくなる。
     * 編集中はセル本体をタブ順から外して、エディタから直接隣のセルへ渡す。
     */
    rootTabIndex(): number | undefined {
      if (this.isReadonly || !this.isTextType) {
        return undefined;
      }
      return this.editing ? -1 : 0;
    },
    /** セル値の文字数上限 (上限のない型は null) */
    valueMaxLength(): number | null {
      return TeachingPlanUtil.cellValueMaxLength(this.cell);
    },
    /**
     * サーバの検証に通らない理由。空文字なら問題なし。
     * 通らない値は親へ「保存できない変更」として渡し、キューには積ませない。
     */
    valueError(): string {
      if (this.isReadonly) {
        return "";
      }
      return TeachingPlanUtil.cellValueError(this.cell, this.draft);
    },
    /** 編集中は内容が収まる高さにする (長文が枠に埋もれないように) */
    editorRows(): number {
      const lines = this.draft.split("\n").length;
      return Math.min(Math.max(lines + 1, 3), 14);
    },
    referenceWarning(): string {
      if (!this.reference) {
        return resolutionLabelOf("ruleMissing", false);
      }
      return resolutionLabelOf(
        this.reference.resolution,
        this.reference.sourcePlanId !== null
      );
    },
    /**
     * 転記元が引けない理由。参照セルと同じ文言にそろえて、
     * 「引き込む」が出ないセルで行き止まりにならないようにする。
     */
    transferWarning(): string {
      if (!this.transfer || this.transfer.resolution === "resolved") {
        return "";
      }
      return resolutionLabelOf(this.transfer.resolution, this.transfer.sourcePlanId !== null);
    },
  },

  watch: {
    value(newValue: string | null) {
      // 入力中の巻き戻しを避ける
      if (this.focused || this.composing) {
        return;
      }
      this.draft = newValue ?? "";
    },
    editable(canEdit: boolean) {
      // 提出・承認で編集不可になったらエディタを閉じる
      if (!canEdit) {
        this.editing = false;
      }
    },
  },

  methods: {
    /** セルのどこをクリックしてもその場でエディタが開くようにする */
    onCellClick() {
      this.$emit("select", this.cell.itemKey || null);
      this.startEdit();
    },
    /**
     * Tabでセルからセルへ移動しながら入力できるようにする。
     * セル本体がフォーカスを受けたらそのままエディタを開き、
     * Tabでエディタから抜けると次のセルが開く (表計算と同じ運指)。
     */
    onCellFocus() {
      this.$emit("select", this.cell.itemKey || null);
      if (this.suppressFocusEdit) {
        this.suppressFocusEdit = false;
        return;
      }
      this.startEdit();
    },
    onCellEnter(event: KeyboardEvent) {
      if (this.editing || this.composing || event.isComposing) {
        return;
      }
      event.preventDefault();
      this.startEdit();
    },
    startEdit() {
      if (this.isReadonly || !this.isTextType || this.editing) {
        return;
      }
      this.editing = true;
      // 前回の変換が確定しないまま閉じていた場合に備えて落としておく
      this.composing = false;
      this.$nextTick(() => {
        const input = this.$refs.input as HTMLInputElement | HTMLTextAreaElement | undefined;
        if (!input) {
          return;
        }
        input.focus();
        // 続きから書けるようにキャレットを末尾へ置く
        const end = input.value.length;
        input.setSelectionRange(end, end);
      });
    },
    /** Esc・Enterでの明示的な終了。blur経由で確定させたうえでセル本体へ戻す */
    closeEditor() {
      const input = this.$refs.input as HTMLInputElement | HTMLTextAreaElement | undefined;
      if (input) {
        input.blur();
      }
      this.editing = false;
      // 閉じた後もTabで次のセルへ進めるようにセル本体へフォーカスを返す
      this.suppressFocusEdit = true;
      this.$nextTick(() => {
        const root = this.$el as HTMLElement | null;
        if (root && typeof root.focus === "function") {
          root.focus();
        } else {
          this.suppressFocusEdit = false;
        }
      });
    },
    /** 変換候補を出している最中のEscはIMEのキャンセルなので、エディタは閉じない */
    onEscKey(event: KeyboardEvent) {
      if (this.composing || event.isComposing) {
        return;
      }
      event.preventDefault();
      this.closeEditor();
    },
    onEnterKey(event: KeyboardEvent) {
      // 日本語変換の確定Enterでエディタを閉じない
      if (this.composing || event.isComposing) {
        return;
      }
      event.preventDefault();
      this.closeEditor();
    },
    onInput(event: Event) {
      const target = event.target as HTMLInputElement | HTMLTextAreaElement;
      // type=number の入力途中の不正状態 (例: "12e") は value が空文字になるため、
      // そのまま流すと画面に文字が見えたまま保存済みの値が削除される。確定できる値になるまで無視する
      if (
        target instanceof HTMLInputElement &&
        target.type === "number" &&
        target.validity.badInput
      ) {
        return;
      }
      this.draft = target.value;
      if (this.composing) {
        return;
      }
      this.emitChange();
    },
    onCompositionEnd(event: CompositionEvent) {
      this.composing = false;
      const target = event.target as HTMLInputElement | HTMLTextAreaElement;
      this.draft = target.value;
      this.emitChange();
    },
    onSelectChange(event: Event) {
      const target = event.target as HTMLInputElement | HTMLSelectElement;
      this.draft = target.value;
      this.emitChange();
    },
    /**
     * 常時コントロールのセル (選択肢・日付・数値) はコントロール自体にフォーカスが入るため、
     * セル本体の @focus/@click では選択セルが更新されない。入力支援パネルの対象が
     * 前のセルのまま残らないよう、コントロール側からも選択を追従させる。
     */
    onControlFocus() {
      this.$emit("select", this.cell.itemKey || null);
    },
    onNumberFocus() {
      this.focused = true;
      this.$emit("select", this.cell.itemKey || null);
    },
    /**
     * フォーカスが外れたときの確定。
     *
     * 変換中にフォーカスが外れると、ブラウザによって compositionend が blur の
     * 前に来たり後に来たりする。どちらでも表示と保存値がずれないように、
     * ここでは「入力要素がその時点で持っている内容」を採用して必ず確定させる。
     * blur が先に来た場合は後続の compositionend が確定後の文字で上書きするが、
     * 自動保存はデバウンスされるため送信されるのは最終的な1回だけになる。
     */
    onBlur(event: FocusEvent) {
      this.focused = false;
      this.editing = false;
      this.composing = false;
      const target = event.target as HTMLInputElement | HTMLTextAreaElement | null;
      if (target && typeof target.value === "string") {
        this.draft = target.value;
      }
      this.emitChange();
    },
    emitChange() {
      if (!this.cell.itemKey || this.isReadonly) {
        return;
      }
      // error があるものは親が保存キューに積まない (積むと400で失敗し続けるため)
      this.$emit("change", {
        itemKey: this.cell.itemKey,
        value: this.draft,
        error: this.valueError,
      });
    },
    /**
     * 入力支援パネルからの本文挿入。
     * 長文セルは改行して末尾に足し、それ以外は1行で収まる必要があるため置き換える。
     *
     * 文例・過去の記入には改行が入りうる。短文セルは改行を持てない (サーバが400)
     * ので、そのまま入れずに空白へ畳んでから置き換える。
     */
    insertText(text: string) {
      if (this.isReadonly) {
        return;
      }
      if (this.cell.type === "longText") {
        this.draft = this.draft ? `${this.draft}\n${text}` : text;
      } else {
        this.draft = text.replace(/\s*\n+\s*/g, " ").trim();
      }
      this.emitChange();
      // 挿入した文をその場で直せるようにエディタを開いておく
      this.startEdit();
    },
  },
});
</script>

<style scoped>
.tp-cell {
  display: flex;
  flex-direction: column;
  min-height: 64px;
  padding: 6px 8px;
  border-right: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
  background-color: #ffffff;
  cursor: pointer;
}
.tp-cell--readonly {
  cursor: default;
}
/* 編集できるセルだけクリックできることが分かるようにする */
.tp-cell:not(.tp-cell--readonly):hover {
  background-color: #f5fbfe;
}
.tp-cell:focus {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: -2px;
}
.tp-cell--selected {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: -2px;
}
/* 保存できない値が残っているセル。スクロールして探せるよう枠でも示す */
.tp-cell--invalid {
  background-color: #fff5f5;
  outline: 2px solid #c62828;
  outline-offset: -2px;
}
.tp-cell--label {
  background-color: #f5f7f8;
  justify-content: center;
}
.tp-cell--label .tp-cell__name {
  font-weight: 700;
}
/*
 * 参照セルは濃青・転記セルは薄青の左罫で区別する (renewal-plan.md B-4/B-5)。
 * 色は §2.4 の転記・参照フロー図に記された値をそのまま使う。
 */
.tp-cell--reference {
  background-color: #eff6fd;
  border-left: 3px solid #42a5f5;
}
.tp-cell--carry {
  background-color: #f9fcff;
  border-left: 3px solid #b3e5fc;
}
.tp-cell--hidden {
  opacity: 0.55;
  background-image: repeating-linear-gradient(
    45deg,
    transparent,
    transparent 6px,
    #f0f0f0 6px,
    #f0f0f0 12px
  );
}
.tp-cell__head {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #607d8b;
  margin-bottom: 2px;
}
.tp-cell__name {
  white-space: pre-wrap;
}
.tp-cell__required {
  color: #c62828;
  font-weight: 700;
  font-size: 10px;
  white-space: nowrap;
}
.tp-cell__badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 0 5px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  white-space: nowrap;
  flex: none;
}
.tp-cell__badge--hidden {
  background-color: #eceff1;
  color: #546e7a;
}
.tp-cell__badge--reference {
  background-color: #e3f2fd;
  color: #1565c0;
}
.tp-cell__badge--carry {
  background-color: #e1f5fe;
  color: #0277bd;
}
.tp-cell__badge--comment {
  background-color: #fff3e0;
  color: #ef6c00;
}
.tp-cell__body {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.tp-cell__label-body {
  flex: 1;
}
.tp-cell__input {
  width: 100%;
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  line-height: 1.6;
  color: #263238;
  resize: vertical;
  font-family: inherit;
}
.tp-cell__input--long {
  min-height: 56px;
}
.tp-cell__text {
  flex: 1;
  font-size: 13px;
  line-height: 1.6;
  color: #263238;
  white-space: pre-wrap;
  overflow-wrap: break-word;
}
.tp-cell__text--empty {
  color: #b0bec5;
}
.tp-cell__text--required {
  color: #ef9a9a;
}
.tp-cell__counter {
  margin-top: 2px;
  font-size: 10px;
  color: #90a4ae;
  text-align: right;
}
.tp-cell__counter--over {
  color: #c62828;
  font-weight: 700;
}
.tp-cell__error {
  margin-top: 4px;
  font-size: 11px;
  font-weight: 700;
  color: #c62828;
}
.tp-cell__input:disabled {
  color: #607d8b;
}
.tp-cell__reference-value {
  font-size: 13px;
  line-height: 1.6;
  color: #263238;
  white-space: pre-wrap;
}
.tp-cell__reference-warning {
  font-size: 12px;
  color: #ef6c00;
}
.tp-cell__reference-source {
  margin-top: 4px;
  font-size: 11px;
  color: #78909c;
}
.tp-cell__transfer-note {
  margin-top: 4px;
  font-size: 11px;
  color: #0277bd;
}
.tp-cell__transfer-note--unresolved {
  color: #ef6c00;
}
/* 「開く」「引き込む」が語中で割れないようにする (「引き」/「込む」になっていた) */
.tp-cell__transfer-note a,
.tp-cell__reference-source a {
  display: inline-block;
  white-space: nowrap;
}
</style>
